import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Check, CheckCircle2, Copy, Inbox, Info, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useT } from "../lib/i18n";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";

export function useLoad(fn, deps = []) {
  const [state, setState] = useState({ data: null, error: null, loading: true });
  // Callers pass `fn` inline with its own explicit deps list (same contract as useCallback), so `fn` itself is not a dependency.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const run = useCallback(fn, deps);
  const reload = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      setState({ data: await run(), error: null, loading: false });
    } catch (e) {
      setState({ data: null, error: errMsg(e), loading: false });
    }
  }, [run]);
  useEffect(() => { reload(); }, [reload]);
  return { ...state, reload };
}

export const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

export function Logo({ testId = "brand-logo" }) {
  const { brand } = useAuth();
  return (
    <span className="inline-flex items-center gap-2.5 select-none" data-testid={testId}>
      <img src="/icons/icon-192.png" alt="" className="h-7 w-7 rounded-[9px] ring-1 ring-white/10 shadow-[0_0_28px_rgba(147,51,234,0.45)]" />
      <span className="font-display text-[17px] font-semibold tracking-[0.06em] text-white">{brand.logo_text}</span>
      <span className="font-serif-lux -ml-1 text-[19px] italic text-purple-200/80">{brand.logo_suffix}</span>
    </span>
  );
}

export function LangToggle({ prefix = "" }) {
  const { lang, setLang } = useT();
  return (
    <div className="inline-flex rounded-full border border-white/10 bg-white/[0.02] p-0.5 text-xs" role="group" aria-label="Language" data-testid={`${prefix}nav-language-toggle`}>
      {["en", "fi"].map((l) => (
        <button key={l} type="button" onClick={() => setLang(l)} aria-pressed={lang === l} data-testid={`${prefix}lang-${l}`}
          className={`min-h-[28px] rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wider transition-colors ${lang === l ? "bg-white text-black" : "text-zinc-400 hover:text-white"}`}>{l}</button>
      ))}
    </div>
  );
}

export function PageHeader({ title, description, actions, eyebrow }) {
  return (
    <div className="mb-8 flex flex-col gap-5 border-b border-white/[0.06] pb-7 sm:mb-10 sm:flex-row sm:items-end sm:justify-between rise">
      <div className="min-w-0">
        <div className="eyebrow mb-3">{eyebrow || "CARIO Mail"}</div>
        <h1 className="text-[28px] font-semibold leading-[1.05] text-white sm:text-4xl lg:text-[44px]" data-testid="page-title">{title}</h1>
        {description && <p className="mt-3 max-w-2xl text-sm leading-relaxed text-zinc-400">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export function Spinner({ label }) {
  const { t } = useT();
  return (
    <div className="flex items-center justify-center gap-2 py-12 text-sm text-zinc-400" role="status" data-testid="loading-state">
      <Loader2 className="h-4 w-4 animate-spin" /> {label || t("common.loading")}…
    </div>
  );
}

export function EmptyState({ icon: Icon = Inbox, title, body, action, testId = "empty-state" }) {
  return (
    <div className="surface flex flex-col items-start gap-3 rounded-2xl p-8 sm:p-10" data-testid={testId}>
      <div className="grid h-11 w-11 place-items-center rounded-xl bg-purple-500/10 ring-1 ring-purple-400/20"><Icon className="h-5 w-5 text-purple-300" /></div>
      <div className="font-display text-lg text-white">{title}</div>
      {body && <p className="max-w-lg text-sm text-zinc-400">{body}</p>}
      {action}
    </div>
  );
}

const TONES = {
  info: ["border-sky-500/20 bg-sky-500/[0.06] text-sky-200", Info],
  warn: ["border-amber-500/25 bg-amber-500/[0.07] text-amber-100", AlertTriangle],
  error: ["border-red-500/25 bg-red-500/[0.07] text-red-100", XCircle],
  ok: ["border-emerald-500/25 bg-emerald-500/[0.06] text-emerald-100", CheckCircle2],
};

export function Notice({ tone = "info", title, children, testId }) {
  const [cls, Icon] = TONES[tone];
  return (
    <div className={`flex gap-3 rounded-xl border p-4 text-sm ${cls}`} role={tone === "error" ? "alert" : "status"} data-testid={testId}>
      <Icon className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="space-y-1">
        {title && <div className="font-semibold">{title}</div>}
        {children && <div className="opacity-90">{children}</div>}
      </div>
    </div>
  );
}

export function ErrorBanner({ error, testId = "error-banner", title }) {
  if (!error) return null;
  return <Notice tone="error" title={title} testId={testId}>{error}</Notice>;
}

export function CopyButton({ value, testId = "copy-button", label }) {
  const { t } = useT();
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setDone(true);
      setTimeout(() => setDone(false), 1500);
    } catch (_) {
      toast.error("Copy failed — select the text and copy manually.");
    }
  };
  return (
    <Button type="button" size="sm" variant="ghost" onClick={copy} data-testid={testId} className="h-7 gap-1 px-2 text-xs text-zinc-300 hover:bg-white/10 hover:text-white">
      {done ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
      {label ?? (done ? t("common.copied") : t("common.copy"))}
    </Button>
  );
}

export function CodeBlock({ code, label, testId = "code-block" }) {
  return (
    <div className="overflow-hidden rounded-xl border border-white/[0.07] bg-[#08070C]" data-testid={testId}>
      <div className="flex items-center justify-between border-b border-white/5 px-3 py-1.5">
        <span className="font-mono text-[11px] uppercase tracking-wider text-zinc-500">{label}</span>
        <CopyButton value={code} testId={`${testId}-copy`} />
      </div>
      <pre className="scroll-thin overflow-x-auto p-4 font-mono text-xs leading-relaxed text-zinc-200"><code>{code}</code></pre>
    </div>
  );
}

const STATUS = {
  found: "bg-emerald-500/10 text-emerald-300 border-emerald-500/25",
  missing: "bg-red-500/10 text-red-300 border-red-500/25",
  wrong_value: "bg-red-500/10 text-red-300 border-red-500/25",
  lookup_timeout: "bg-amber-500/10 text-amber-300 border-amber-500/25",
  verified: "bg-emerald-500/10 text-emerald-300 border-emerald-500/25",
  delivered: "bg-emerald-500/10 text-emerald-300 border-emerald-500/25",
  active: "bg-emerald-500/10 text-emerald-300 border-emerald-500/25",
  accepted: "bg-sky-500/10 text-sky-300 border-sky-500/25",
  queued: "bg-zinc-500/10 text-zinc-300 border-zinc-500/25",
  pending: "bg-amber-500/10 text-amber-300 border-amber-500/25",
  retrying: "bg-amber-500/10 text-amber-300 border-amber-500/25",
  held: "bg-amber-500/10 text-amber-300 border-amber-500/25",
  paused: "bg-zinc-500/10 text-zinc-300 border-zinc-500/25",
  simulated: "bg-fuchsia-500/10 text-fuchsia-300 border-fuchsia-500/30",
  bounced: "bg-red-500/10 text-red-300 border-red-500/25",
  complained: "bg-red-500/10 text-red-300 border-red-500/25",
  failed: "bg-red-500/10 text-red-300 border-red-500/25",
  revoked: "bg-red-500/10 text-red-300 border-red-500/25",
  draft: "bg-zinc-500/10 text-zinc-300 border-zinc-500/25",
  scheduled: "bg-sky-500/10 text-sky-300 border-sky-500/25",
  sending: "bg-purple-500/10 text-purple-200 border-purple-500/30",
  testing: "bg-fuchsia-500/10 text-fuchsia-300 border-fuchsia-500/30",
  sent: "bg-emerald-500/10 text-emerald-300 border-emerald-500/25",
  cancelled: "bg-zinc-500/10 text-zinc-400 border-zinc-500/25",
  suppressed: "bg-amber-500/10 text-amber-300 border-amber-500/25",
};

const STATUS_FI = { verified: "vahvistettu", delivered: "toimitettu", active: "aktiivinen", accepted: "hyväksytty", queued: "jonossa", pending: "odottaa",
  retrying: "yritetään uudelleen", held: "pidätetty", paused: "keskeytetty", simulated: "testi · ei lähetetty", bounced: "palautunut", complained: "valitus",
  failed: "epäonnistui", revoked: "peruttu", draft: "luonnos", scheduled: "ajastettu", sending: "lähetetään", testing: "testataan", sent: "lähetetty", cancelled: "peruttu", suppressed: "estetty", not_started: "ei aloitettu", unknown: "tuntematon" };

export function StatusBadge({ status, testId }) {
  const { lang } = useT();
  const label = (lang === "fi" && STATUS_FI[status]) || (status === "simulated" ? "test · not sent" : String(status || "unknown").replaceAll("_", " "));
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${STATUS[status] || "border-white/10 text-zinc-300"}`} data-testid={testId}>
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-80" />{label}
    </span>
  );
}

export function SecretReveal({ open, onClose, title, secret, note, testId = "secret-reveal" }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-white/10 bg-[#0F0E13] sm:max-w-lg" data-testid={testId}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{note || "Copy it now. For your security it will never be shown again."}</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 rounded-lg border border-purple-500/30 bg-purple-950/20 p-3">
          <code className="flex-1 break-all font-mono text-xs text-purple-100" data-testid={`${testId}-value`}>{secret}</code>
          <CopyButton value={secret || ""} testId={`${testId}-copy`} />
        </div>
        <DialogFooter>
          <Button onClick={onClose} data-testid={`${testId}-done`}>I have stored it safely</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function Card({ children, className = "", testId }) {
  return <div className={`surface rounded-2xl p-5 sm:p-7 ${className}`} data-testid={testId}>{children}</div>;
}
